/**
 * Frontend API Error Utilities
 *
 * Error types and handling for API responses from raw fetch().
 */

/**
 * Error thrown when the user's session has expired or is invalid (401)
 * This error is handled globally by the QueryClient to clear the cache
 */
export class AuthenticationError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * API Error that matches the expected ApiError interface from generated hooks.
 * This allows error handlers to access error.error consistently.
 */
export interface ExistingCustomerInfo {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  matchedOn: ('phone' | 'email' | 'name')[];
}

export class DuplicateCustomerError extends Error {
  readonly existingCustomer: ExistingCustomerInfo;

  constructor(existingCustomer: ExistingCustomerInfo) {
    super('A similar customer already exists.');
    this.name = 'DuplicateCustomerError';
    this.existingCustomer = existingCustomer;
  }
}

export class ApiErrorResponse extends Error {
  /** The error message (matches ApiError interface) */
  readonly error: string;
  /** HTTP status code (0 if not from an HTTP response) */
  readonly status: number;
  /** Optional field-level validation errors */
  readonly fields?: Record<string, string[]>;

  constructor(message: string, status = 0, fields?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiErrorResponse';
    this.error = message;
    this.status = status;
    this.fields = fields;
  }
}

export interface PaymentMethodRequiredPayload {
  pricePerRo: number;
  allowance: number;
  used: number;
}

export class DiscountStackingConflictError extends Error {
  readonly conflictingDiscountIds: string[];

  constructor(conflictingDiscountIds: string[]) {
    super('This discount conflicts with existing discounts');
    this.name = 'DiscountStackingConflictError';
    this.conflictingDiscountIds = conflictingDiscountIds;
  }
}

export class CouponAlreadyAppliedError extends Error {
  readonly existingDiscountId: string;

  constructor(existingDiscountId: string) {
    super('This coupon is already applied to this repair order');
    this.name = 'CouponAlreadyAppliedError';
    this.existingDiscountId = existingDiscountId;
  }
}

export class PaymentMethodRequiredError extends Error {
  readonly code = 'payment_method_required' as const;
  readonly pricePerRo: number;
  readonly allowance: number;
  readonly used: number;

  constructor(payload: PaymentMethodRequiredPayload) {
    super('payment_method_required');
    this.name = 'PaymentMethodRequiredError';
    this.pricePerRo = payload.pricePerRo;
    this.allowance = payload.allowance;
    this.used = payload.used;
  }
}

/**
 * Codes the backend `BlockedStageTransitionError` (apps/api) emits when a
 * PATCH `/repair-orders/:id` is rejected because the requested stage move is
 * accounting-illegal (e.g. closed → anything, invoice → earlier) or violates
 * the Phase 2A workflow protections. Mirrors the server-side
 * `BlockedTransitionCode` union — keep in sync with
 * apps/api/modules/repair-orders/domain/validate-stage-change.ts.
 */
export type BlockedStageTransitionCode =
  | 'CLOSED_TO_ANY'
  | 'INVOICE_TO_EARLIER'
  | 'IN_PROGRESS_TO_INTAKE'
  | 'MUST_USE_CLOSE_ROUTE'
  | 'MUST_BE_APPROVED_TO_START';

/**
 * Thrown for HTTP 422 responses with `error: 'BLOCKED_STAGE_TRANSITION'`.
 * Preserves the structured `code` and human-readable `message` so feature-level
 * onError handlers (e.g. `useChangeStage`) can surface the backend's exact copy
 * in a destructive toast for non-drag-drop paths (mobile pipeline, programmatic
 * stage changes). The drag-drop UI blocks these moves client-side via
 * transition rules and never hits this throw.
 */
export class BlockedStageTransitionError extends Error {
  readonly code: BlockedStageTransitionCode;
  readonly status = 422 as const;

  constructor(code: BlockedStageTransitionCode, message: string) {
    super(message);
    this.name = 'BlockedStageTransitionError';
    this.code = code;
  }
}

/**
 * Reasons the backend's `RoNotConvertibleError` (apps/api) emits when a
 * `mark_work_complete` advance, PATCH stage move, or convert-to-invoice call
 * is rejected because the RO can't be invoiced yet. The backend sends the
 * reason as `<stage>/<slug>` in the `message` field; we parse out the slug to
 * pick user-facing copy. Keep in sync with the slugs emitted by
 * apps/api/modules/repair-orders/routes/workflow/advance-workflow.ts and
 * apps/api/modules/repair-orders/routes/convert-to-invoice.ts.
 */
export type RoNotConvertibleReason = 'no-approved-jobs' | 'no-billable-content' | 'labor-incomplete' | 'unknown';

/**
 * Thrown for HTTP 422 responses with `error: 'RO_NOT_CONVERTIBLE'`. The
 * backend's pre-validation rejects in_progress → invoice and intake → invoice
 * moves whose approved jobs don't yet have billable content or completed
 * labor. The drag-drop UI normally catches these client-side via
 * `toInvoiceRule`, but programmatic paths (the per-card "Next Stage" action,
 * stale-cache races) need a typed error so they can render the same block
 * dialog instead of the generic "Failed to move repair order" toast.
 */
export class RoNotConvertibleError extends Error {
  readonly reason: RoNotConvertibleReason;
  readonly status = 422 as const;

  constructor(reason: RoNotConvertibleReason, message: string) {
    super(message);
    this.name = 'RoNotConvertibleError';
    this.reason = reason;
  }
}

/**
 * Parses the backend's `<stage>/<reason>` message into the structured reason
 * slug. Unknown shapes fall back to `'unknown'` so callers always render
 * *something* meaningful instead of leaking the raw `in_progress/...` string.
 */
export const parseRoNotConvertibleReason = (raw: string | undefined): RoNotConvertibleReason => {
  if (!raw) {
    return 'unknown';
  }
  const slug = raw.includes('/') ? raw.slice(raw.indexOf('/') + 1) : raw;
  if (slug === 'no-approved-jobs' || slug === 'no-billable-content' || slug === 'labor-incomplete') {
    return slug;
  }
  return 'unknown';
};

/**
 * Codes the backend emits for a sub-stage move that violates the Phase 2A
 * intake-locking protections (after a customer has approved or declined an
 * estimate, the intake sub-stage is locked) or the Phase 4A/4B content
 * invariants (chips that imply state — invoice/payment chips need an invoice,
 * intake/approved needs ≥1 approved job, intake/inspection_* needs a DVI,
 * intake/waiting_for_approval needs an estimate sent, intake/declined needs a
 * declined job, in_progress chips need their backing record, etc.).
 * Mirrors the server-side `BlockedSubStageCode` union — keep in sync with
 * apps/api/modules/repair-orders/domain/validate-sub-stage-change.ts.
 */
export type BlockedSubStageTransitionCode =
  | 'INTAKE_APPROVED_LOCKED'
  | 'INTAKE_DECLINED_LOCKED'
  | 'SUB_STAGE_REQUIRES_INVOICE'
  | 'SUB_STAGE_APPROVED_REQUIRES_APPROVED_JOB'
  // Phase 4B
  | 'SUB_STAGE_REQUIRES_ACTIVE_DVI'
  | 'SUB_STAGE_REQUIRES_COMPLETED_DVI'
  | 'SUB_STAGE_REQUIRES_ESTIMATE_SENT'
  | 'SUB_STAGE_DECLINED_REQUIRES_DECLINED_JOB'
  | 'SUB_STAGE_REQUIRES_PENDING_PARTS_ORDER'
  | 'SUB_STAGE_REQUIRES_PENDING_ADDON'
  | 'SUB_STAGE_REQUIRES_SUBLET'
  | 'SUB_STAGE_PICKUP_REQUIRES_PAYMENT_OR_PICKEDUP';

/**
 * Thrown for HTTP 422 responses with `error: 'BLOCKED_SUB_STAGE_TRANSITION'`.
 * Preserves the structured `code` and human-readable `message` so feature-level
 * onError handlers (e.g. `useMoveSubStage` consumers) can surface the backend's
 * exact copy in the block dialog modal instead of a generic toast.
 */
export class BlockedSubStageTransitionError extends Error {
  readonly code: BlockedSubStageTransitionCode;
  readonly status = 422 as const;

  constructor(code: BlockedSubStageTransitionCode, message: string) {
    super(message);
    this.name = 'BlockedSubStageTransitionError';
    this.code = code;
  }
}

/**
 * Codes the backend reopen route emits when the request is rejected for a
 * reason the user can act on (RO not in a reopenable stage, paid invoice,
 * closure-reason matrix mismatch). Mirrors the server-side reopen error
 * classes — keep in sync with
 * apps/api/modules/repair-orders/errors/repair-orders.errors.ts.
 */
export type ReopenErrorCode = 'RO_NOT_REOPENABLE' | 'REOPEN_NOT_ALLOWED_PAID' | 'INVALID_REOPEN_TARGET';

/**
 * Thrown for HTTP 422 responses from `POST /repair-orders/:id/reopen` whose
 * `error` discriminator is one of the structured reopen codes. Preserves the
 * `code` and human-readable `message` so feature-level onError handlers (e.g.
 * `ReopenDialog`) can surface the backend's exact copy in a destructive toast
 * rather than the raw `error` string. The dialog itself blocks most cases
 * client-side via the closure-reason matrix; this class covers the residual
 * paths (paid invoice mid-flow, race against another user closing/voiding).
 */
export class ReopenError extends Error {
  readonly code: ReopenErrorCode;
  readonly status = 422 as const;

  constructor(code: ReopenErrorCode, message: string) {
    super(message);
    this.name = 'ReopenError';
    this.code = code;
  }
}

/**
 * Thrown for HTTP 402 responses with `code: 'tier_upgrade_required'` (e.g., a
 * Free/Pro org attempting to add a second shop). Carries the tier the caller
 * needs to upgrade to so the UI can render a targeted CTA.
 */
export class TierUpgradeRequiredError extends Error {
  readonly code = 'tier_upgrade_required' as const;
  readonly status = 402 as const;
  readonly requiredTier: string;

  constructor(requiredTier: string) {
    super('tier_upgrade_required');
    this.name = 'TierUpgradeRequiredError';
    this.requiredTier = requiredTier;
  }
}

/**
 * Mirrors the backend's `lastConsentStatus` discriminator on the 409
 * `needs_optin` response. `never` means no consent row was ever recorded for
 * this customer; `opted_out` means the most recent row is a stop reply (or
 * explicit revoke).
 */
export type NeedsOptInLastConsentStatus = 'never' | 'opted_out';

/**
 * Thrown for HTTP 409 responses with `code: 'needs_optin'` from any
 * SMS-sending endpoint (estimate send, invoice send, conversation send).
 * Preserves `lastConsentStatus` so the UI can pick copy ("hasn't opted in" vs
 * "previously opted out") and surface a "Send opt-in request" CTA inline
 * instead of toasting a generic error.
 */
export class NeedsOptInError extends Error {
  readonly code = 'needs_optin' as const;
  readonly status = 409 as const;
  readonly lastConsentStatus: NeedsOptInLastConsentStatus;

  constructor(lastConsentStatus: NeedsOptInLastConsentStatus, message = 'Customer has not opted in to SMS') {
    super(message);
    this.name = 'NeedsOptInError';
    this.lastConsentStatus = lastConsentStatus;
  }
}

/**
 * The granularity at which a pricing-matrix association overlaps another
 * enabled matrix. Mirrors the backend's `AssociationGranularity` —
 * apps/api/modules/pricing-matrices/lib/find-association-conflict.ts.
 */
export type MatrixAssociationGranularity = 'category' | 'subcategory' | 'part-type';

/**
 * Thrown for HTTP 409 responses with `code: 'MATRIX_ASSOCIATION_CONFLICT'` from
 * `PUT /pricing-matrices/:id`. To keep parts pricing deterministic, two enabled
 * matrices may not hold associations at the same granularity for the same
 * taxonomy node (ADR-0016). Preserves the conflicting node so the page can
 * surface a precise message naming it instead of a generic error. `nodeName`
 * may be null when the backend couldn't resolve a human-readable label.
 */
export class MatrixAssociationConflictError extends Error {
  readonly code = 'MATRIX_ASSOCIATION_CONFLICT' as const;
  readonly status = 409 as const;
  readonly granularity: MatrixAssociationGranularity;
  readonly nodeId: string;
  readonly nodeName: string | null;

  constructor(granularity: MatrixAssociationGranularity, nodeId: string, nodeName: string | null, message: string) {
    super(message);
    this.name = 'MatrixAssociationConflictError';
    this.granularity = granularity;
    this.nodeId = nodeId;
    this.nodeName = nodeName;
  }
}
